import React, { createContext, useState, useEffect } from 'react';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { useLocation } from 'cccisd-react-router';
import progressQuery from './progress.graphql';

const Fortress = window.cccisd.fortress;
const CP_BASELINE_DEPLOYMENT_HANDLE = 'baseline_cp';
const CP_INTERVENTION_DEPLOYMENT_HANDLE = 'intervention_cp';

export const CPProgressContext = createContext({
    isBaselineDone: null,
    flowVariables: {},
    cpBaselineHash: '',
    cpBaselineDeploymentHandle: CP_BASELINE_DEPLOYMENT_HANDLE,
    cpInterventionHash: '',
    cpInterventionDeploymentHandle: CP_INTERVENTION_DEPLOYMENT_HANDLE,
    refetchData: () => {},
    isLoaded: false,
});

// expect devTags to match a `handle` of some defined flowVariable
const possibleFlowVariables = window.cccisd.appDefs.flow.variables;

const AppWrapper = props => {
    // cannot use `useParams` yet because we're not inside of AppRoute
    const location = useLocation();
    const respondentHashMatch = location.pathname.match(/^\/d\/[A-z0-9]{7}\/([A-z0-9]{7})$/);
    const respondentHash = respondentHashMatch ? respondentHashMatch[1] : Fortress.user.acting.respondent_hash;

    // if Anon deployment or guest, it will try to look up devTags for guest user and find nothing, so who cares...
    if (!respondentHash) {
        return props.children;
    }

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    async function getData() {
        const resp = await client.query({
            query: progressQuery,
            variables: {
                respondentHash,
                baselineHandle: CP_BASELINE_DEPLOYMENT_HANDLE,
                interventionHandle: CP_INTERVENTION_DEPLOYMENT_HANDLE,
            },
            fetchPolicy: 'network-only',
        });

        setData(resp.data);
        setIsLoading(false);
    }

    useEffect(() => {
        getData();
    }, [location.pathname]);

    if (isLoading || !data) {
        return <Loader loading />;
    }

    const deploymentList = Array.isArray(data?.deployments?.deploymentList) ? data.deployments.deploymentList : [];
    const ap = data.roles?.anyRole?.assignmentProgress || {};
    const isBaselineDone = !!ap?.completed;
    const devTagValues = ap?.devTags || {};

    const baselineHash = deploymentList.find(dep => dep.deploymentHandle === CP_BASELINE_DEPLOYMENT_HANDLE)?.hash;
    if (!baselineHash) {
        console.error(
            'Could not find deployment hash for CP Baseline deployment. Check that handle ' +
                CP_BASELINE_DEPLOYMENT_HANDLE +
                ' has been applied.'
        );
    }

    const interventionHash = deploymentList.find(
        dep => dep.deploymentHandle === CP_INTERVENTION_DEPLOYMENT_HANDLE
    )?.hash;
    if (!interventionHash) {
        console.error(
            'Could not find deployment hash for CP Intervention deployment. Check that handle ' +
                CP_INTERVENTION_DEPLOYMENT_HANDLE +
                ' has been applied.'
        );
    }

    return (
        <CPProgressContext.Provider
            value={{
                isBaselineDone,
                refetchData: getData,
                cpBaselineHash: baselineHash,
                cpBaselineDeploymentHandle: CP_BASELINE_DEPLOYMENT_HANDLE,
                cpInterventionHash: interventionHash,
                cpInterventionDeploymentHandle: CP_INTERVENTION_DEPLOYMENT_HANDLE,
                isLoaded: true,

                // mapping of { VARIABLE_NAME : VALUE }
                flowVariables: possibleFlowVariables.reduce((all, curr) => {
                    const { handle } = curr;
                    if (handle in devTagValues) {
                        let val = devTagValues[handle];
                        if (!Number.isNaN(parseInt(val, 10))) {
                            val = parseInt(val, 10);
                        }
                        all[handle] = val;
                    }
                    return all;
                }, {}),
            }}
        >
            {props.children}
        </CPProgressContext.Provider>
    );
};

export default AppWrapper;
